import { useState } from 'react';
import { Star } from 'lucide-react';
import api from '../../services/api';

interface Props {
  productId: number;
  /** Called after a successful submit so the parent can refetch the list. */
  onSubmitted?: () => void;
  className?: string;
}

/**
 * Minimal "Write a review" form. Posts to the public storefront endpoint
 * `POST /store/products/:productId/reviews`. Server-side validation +
 * 24h dedupe per email + verified-buyer auto-flag. Keeps frontend dumb.
 *
 * Image input is a paste-URL field (not file upload). Adding file upload
 * later means swapping the input for a media-upload component without
 * touching the submit logic.
 */
export function SubmitReviewForm({ productId, onSubmitted, className = '' }: Props) {
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [authorName, setAuthorName] = useState('');
  const [authorEmail, setAuthorEmail] = useState('');
  const [text, setText] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/store/products/${productId}/reviews`, {
        rating,
        authorName: authorName.trim(),
        authorEmail: authorEmail.trim() || undefined,
        text: text.trim(),
        imageUrl: imageUrl.trim() || undefined,
      });
      setAuthorName('');
      setAuthorEmail('');
      setText('');
      setImageUrl('');
      setRating(5);
      setDone(true);
      onSubmitted?.();
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      setErr(
        Array.isArray(msg) ? msg.join('. ') : msg ?? 'Could not submit review. Try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        className={`rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 ${className}`}
        role="status"
      >
        Thanks — your review is live. It may take a moment to appear above.
        <button
          type="button"
          onClick={() => setDone(false)}
          className="ml-2 font-semibold underline-offset-4 hover:underline"
        >
          Write another
        </button>
      </div>
    );
  }

  const displayRating = hoverRating || rating;

  return (
    <form
      onSubmit={submit}
      className={`grid gap-3 rounded-2xl border p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.06)] ${className}`}
      style={{
        background: 'color-mix(in srgb, currentColor 4%, transparent)',
        borderColor: 'color-mix(in srgb, currentColor 14%, transparent)',
        color: 'inherit',
      }}
      aria-label="Write a review"
    >
      <h3 className="text-base font-semibold">Write a review</h3>

      <fieldset className="flex items-center gap-2" aria-label="Rating">
        <span className="text-sm" style={{ opacity: 0.7 }}>Rating</span>
        <div className="flex items-center gap-0.5" onMouseLeave={() => setHoverRating(0)}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onMouseEnter={() => setHoverRating(n)}
              onClick={() => setRating(n)}
              className="p-1 -m-1"
              aria-label={`${n} star${n === 1 ? '' : 's'}`}
            >
              <Star
                className={`w-5 h-5 transition-colors ${
                  n <= displayRating ? 'fill-amber-500 text-amber-500' : 'text-neutral-300'
                }`}
              />
            </button>
          ))}
        </div>
      </fieldset>

      <input
        type="text"
        required
        minLength={2}
        maxLength={80}
        value={authorName}
        onChange={(e) => setAuthorName(e.target.value)}
        placeholder="Your name"
        className="rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2"
        style={{
          background: 'color-mix(in srgb, currentColor 6%, transparent)',
          borderColor: 'color-mix(in srgb, currentColor 14%, transparent)',
          color: 'inherit',
        }}
      />

      <input
        type="email"
        value={authorEmail}
        onChange={(e) => setAuthorEmail(e.target.value)}
        placeholder="Email (optional — used to mark verified buyer)"
        className="rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2"
        style={{
          background: 'color-mix(in srgb, currentColor 6%, transparent)',
          borderColor: 'color-mix(in srgb, currentColor 14%, transparent)',
          color: 'inherit',
        }}
      />

      <textarea
        required
        minLength={10}
        maxLength={1500}
        rows={4}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="What did you think?"
        className="rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 resize-y"
        style={{
          background: 'color-mix(in srgb, currentColor 6%, transparent)',
          borderColor: 'color-mix(in srgb, currentColor 14%, transparent)',
          color: 'inherit',
        }}
      />

      <input
        type="url"
        value={imageUrl}
        onChange={(e) => setImageUrl(e.target.value)}
        placeholder="Image URL (optional)"
        className="rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2"
        style={{
          background: 'color-mix(in srgb, currentColor 6%, transparent)',
          borderColor: 'color-mix(in srgb, currentColor 14%, transparent)',
          color: 'inherit',
        }}
      />

      {err && (
        <p role="alert" className="text-sm text-red-700">
          {err}
        </p>
      )}

      <button
        type="submit"
        disabled={busy}
        className="self-start inline-flex items-center justify-center px-5 py-3 rounded-full font-semibold text-white shadow-md hover:shadow-lg active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--primary-color)]"
        style={{ background: 'var(--primary-color, #f97316)' }}
      >
        {busy ? 'Submitting…' : 'Submit review'}
      </button>
    </form>
  );
}
